import React, { FC, useCallback, useReducer, useEffect } from 'react';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { useParams } from 'react-router-dom';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { find, filter, compose, not, propEq } from 'ramda';
import { action } from 'typesafe-actions';
import { transactionService, authorizationService } from '../../../../../channels/secure-web/services';
import { ConsolidatedTransactionDetails } from '../../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { shouldShowRequestRateBtn } from '../../../../fundTransfer/src/utils';
import { Mandate } from '../../domain/Authorization/entities';
import { CheckMandateResponseBody } from '../../domain/Authorization/entities/CheckMandateResponseDTO';
import { ProcessStatuses } from '../../components/ApproveRejectAuthFlowPresenter';
import AuthorizationContext from './AuthorizationContext';
import { AuthorizationReducer } from './AuthorizationReducers';
import { defaultAuthorizationProviderState } from './defaultState';
import { AuthorizationActionTypes } from './types';

const AuthorizationProvider: FC<{ authorizationsList: Nullable<Mandate[]> }> = ({ authorizationsList, children }) => {
  const { transactionNo } = useParams();
  const [state, dispatch] = useReducer(AuthorizationReducer, defaultAuthorizationProviderState);

  const shouldRequestTransactionDetails =
    Boolean(state?.selectedAuthorization) &&
    !isEmpty(state?.selectedAuthorization) &&
    Boolean(state?.selectedAuthorization?.globalOrderId);

  const {
    data: transactionDetails,
    error: transactionDetailsError,
    isLoading: isLoadingTransactionDetails,
    mutate: transactionDetailsMutation,
  } = useInvokeSWRService<ConsolidatedTransactionDetails>(
    transactionService.GetConsolidatedTransactionDetails,
    {
      orderId: Number(state.selectedAuthorization?.globalOrderId),
      accountNumber: state.selectedAuthorization?.accountNumber,
      meta: {
        timestamp: state.selectedAuthorization?.timestamp,
      },
    },
    shouldRequestTransactionDetails,
  );

  const shouldCheckAuthorization =
    Boolean(state.selectedAuthorization) && state.selectedAuthorization !== null && Boolean(transactionDetails);

  // Check Authorization
  const {
    data: checkAuthorizationData,
    isLoading: isLoadingCheckAuthorization,
    error: checkAuthorizationError,
    mutate: checkAuthorizationMutation,
  } = useInvokeSWRService<CheckMandateResponseBody>(
    authorizationService.CheckMandate,
    {
      accountNumber: state.selectedAuthorization?.accountNumber,
      uniqueId: Number(state.selectedAuthorization?.transactionId),
      amount: Number(state.selectedAuthorization?.amount),
      meta: {
        timestamp: state.selectedAuthorization?.timestamp,
      },
    },
    shouldCheckAuthorization,
  );

  const onSelectAuthorization = useCallback(
    (transactionId?: string) => {
      if (transactionId && authorizationsList) {
        const authorization = find(
          authorization => authorization.transactionId === transactionId,
          authorizationsList as Mandate[],
        );
        dispatch(
          action(AuthorizationActionTypes.SetSelectedAuthorization, {
            ...authorization,
            timestamp: state?.selectedAuthorization?.timestamp || Date.now().toString(),
          }),
        );
      }
    },
    [authorizationsList, state?.selectedAuthorization?.timestamp],
  );

  const onBulkAuthorizationSelect = useCallback(
    (transactionId: string) => {
      const bulkAuthInState = state?.bulkAuthorizations?.find(ba => ba.transactionId === transactionId);
      if (bulkAuthInState) {
        const bulkActionsToSetInState = filter(
          compose(not, propEq('transactionId', transactionId)),
          state?.bulkAuthorizations as Mandate[],
        );
        dispatch(
          action(
            AuthorizationActionTypes.SetBulkAuthorizations,
            !isEmpty(bulkActionsToSetInState) ? bulkActionsToSetInState : null,
          ),
        );
      } else {
        const mandateFound = authorizationsList?.find(a => a.transactionId === transactionId);
        dispatch(action(AuthorizationActionTypes.SetBulkAuthorizations, mandateFound ? mandateFound : null));
      }
    },
    [state.bulkAuthorizations, authorizationsList],
  );

  const onMassiveAuthorizationSelection = useCallback(
    (selectAllAuthorizations: boolean) => {
      if (selectAllAuthorizations) {
        const bulkActionsToSetInState = filter(
          compose(not, propEq('processStatus', ProcessStatuses.PENDING)),
          authorizationsList as Mandate[],
        );
        dispatch(action(AuthorizationActionTypes.SetBulkAuthorizations, bulkActionsToSetInState));
      } else {
        dispatch(action(AuthorizationActionTypes.SetBulkAuthorizations, null));
      }
    },
    [authorizationsList],
  );

  const onSetRequestNewRate = useCallback(
    (value: boolean) => dispatch(action(AuthorizationActionTypes.SetRequestSpecialRate, value)),
    [],
  );
  const onSetAuthAction = useCallback(
    (actionName: Nullable<string>) => dispatch(action(AuthorizationActionTypes.SetAuthAction, actionName)),
    [],
  );

  const initiateMandateService = useCallback(() => authorizationService.InitiateMandate, []);
  const initiateBulkMandatesService = useCallback(() => authorizationService.InitiateBulkMandates, []);
  const executeMandateService = useCallback(() => authorizationService.ExecuteMandate, []);
  const executeBulkMandatesService = useCallback(() => authorizationService.ExecuteBulkMandates, []);

  const onToggleAuthFlowDrawer = useCallback(() => {
    if (state.isAuthFlowDrawerOpen) {
      onSetRequestNewRate(false);
      onSetAuthAction(null);
      dispatch(action(AuthorizationActionTypes.SetBulkAuthorizations, null));
    }
    dispatch(action(AuthorizationActionTypes.SetAuthFlowDrawerOpen, !state.isAuthFlowDrawerOpen));
  }, [state.isAuthFlowDrawerOpen, onSetRequestNewRate, onSetAuthAction]);

  const shouldShowRequestNewRateBtn = shouldShowRequestRateBtn({
    transferType: state?.selectedAuthorization?.transferType ?? null,
    orderCurrency: state?.selectedAuthorization?.currency,
    debitCurrency: transactionDetails?.accountCurrency,
    isLastAuthorizer: !!checkAuthorizationData?.lastAuthorizer,
    isEligibleForDealerRate: !!state?.selectedAuthorization?.eligibleForDealerRate,
  });

  const onTransactionDetailsMutation = useCallback(() => {
    transactionDetailsMutation && transactionDetailsMutation();
  }, [transactionDetailsMutation]);

  const onCheckAuthorizationMutation = useCallback(() => {
    checkAuthorizationMutation && checkAuthorizationMutation();
  }, [checkAuthorizationMutation]);

  const toggleAuthorizationTransactionDetailsDrawer = useCallback(() => {
    if (state.isAuthorizationTransactionDetailsDrawerOpen) {
      dispatch(action(AuthorizationActionTypes.SetSelectedAuthorization, null));
      onSetRequestNewRate(false);
      onSetAuthAction(null);
    }
    dispatch(
      action(
        AuthorizationActionTypes.SetAuthorizationTransactionDetailsDrawer,
        !state.isAuthorizationTransactionDetailsDrawerOpen,
      ),
    );
  }, [state.isAuthorizationTransactionDetailsDrawerOpen, onSetRequestNewRate, onSetAuthAction]);

  // get selected Authorization from params
  useEffect(() => {
    if (transactionNo && authorizationsList) {
      const foundAuthorization = authorizationsList.find(auth => String(auth.globalOrderId) === transactionNo);
      if (foundAuthorization) {
        dispatch(action(AuthorizationActionTypes.SetSelectedAuthorization, foundAuthorization));
      }
    }
  }, [authorizationsList, transactionNo]);

  const values = {
    ...state,
    onSelectAuthorization,
    authorizationsList: authorizationsList ?? [],
    checkAuthorizationData,
    transactionDetails: transactionDetails ?? null,
    loadingState: {
      isLoadingTransactionDetails,
      isLoadingCheckAuthorization,
    },
    errorState: {
      transactionDetailsError,
      checkAuthorizationError,
    },
    toggleAuthorizationTransactionDetailsDrawer,
    onToggleAuthFlowDrawer,
    onTransactionDetailsMutation,
    initiateMandateService,
    initiateBulkMandatesService,
    executeBulkMandatesService,
    executeMandateService,
    onCheckAuthorizationMutation,
    onBulkAuthorizationSelect,
    onMassiveAuthorizationSelection,
    onSetRequestNewRate,
    onSetAuthAction,
    shouldShowRequestNewRateBtn,
  };

  return <AuthorizationContext.Provider value={values}>{children}</AuthorizationContext.Provider>;
};

export { AuthorizationProvider };
